// masterySurfaceMap — for every one of the 100 mastery skills, names
// the single HackerLabs surface a beginner should open right now, the
// button label that takes them there, and a one-line micro-prompt or
// example so they know what to actually try.
//
// Keeping this as a separate file (instead of mutating
// AI_MASTERY_PATH) lets us evolve the surface mapping independently
// of the canonical 100-skill labels (which are a stable promise to
// the user). The shell looks up surface info by skill `num`.
//
// Conventions for `surface`:
//   - "/?skill=<label>"   → home runner with playbook auto-launched
//                           (the original Beginner Mode behavior).
//                           Use this when there's no better dedicated
//                           surface yet.
//   - "/hub?tab=<mode>"   → AI Hub tab (chat / image / research /
//                           analyze / builder). The Hub already reads
//                           ?tab= and switches modes.
//   - "/planner"          → Easy Mode Task Planner.
//   - "/agents/builder"   → Agent builder (with optional ?goal= or
//                           ?template= pre-fills).
//   - "/library"          → Forkable playbook library.
//   - "/quests"           → Hands-on guided quests.
//   - "/power21"          → 21-day systematic sprint.
//   - "/projects"         → User project workspace.
//   - "/squads"           → Squad-based collaboration.
//   - "/compare"          → Multi-model comparison.
//   - "/profile/:me"      → Public profile (share & XP).
//
// The `cta` is the literal button label. The `microPrompt` is shown
// inline as a "Try this" example — kept short, conversational, and
// always something a beginner could literally paste.

export interface SkillSurface {
  surface: string;
  cta: string;
  microPrompt: string;
}

// Helper to build a /?skill= surface from the canonical label.
const home = (label: string, microPrompt: string): SkillSurface => ({
  surface: `/?skill=${encodeURIComponent(label)}`,
  cta: "Open the guided chat",
  microPrompt,
});

const hub = (
  mode: "chat" | "image" | "research" | "analyze" | "builder",
  microPrompt: string,
  cta?: string,
): SkillSurface => ({
  surface: `/hub?tab=${mode}`,
  cta: cta ?? `Open ${mode}`,
  microPrompt,
});

// The full 100-skill map. Order MUST match AI_MASTERY_PATH numbering.
// We intentionally don't fall back to a default — every skill gets a
// hand-picked surface so beginners always know exactly what to do.
export const MASTERY_SURFACE_MAP: Record<number, SkillSurface> = {
  // ───────── Awareness 1–20 ─────────
  1: home("What is AI (LLMs vs ML vs Automation)", "What can you actually do for me?"),
  2: home("Difference between prompt vs program", "Show me a prompt vs a program with the same goal."),
  3: hub("chat", "Plan my week using bullet points and emojis."),
  4: hub("chat", "Rewrite this email to sound friendlier: [paste your email]"),
  5: hub("chat", "Help me brainstorm 5 birthday gifts for my mom — she loves gardening."),
  6: hub("chat", "Tell me 3 things you might get wrong about Iceland's history."),
  7: hub("analyze", "Paste any article — I'll summarize it in 3 bullets.", "Open analyze"),
  8: hub("chat", "Rewrite this in plain English for a 12-year-old: [paste]"),
  9: hub("chat", "Brainstorm 10 weekend project ideas for someone learning Python."),
  10: hub("chat", "Teach me what 'compound interest' means in 60 seconds."),
  11: hub("chat", "Act as a 1990s sports commentator. Describe my morning coffee."),
  12: hub("chat", "Take your last answer and make it half as long."),
  13: { surface: "/compare", cta: "Open compare", microPrompt: "Ask the same question to 3 models — see who wins." },
  14: hub("chat", "Walk me through this complex topic step-by-step: [your topic]"),
  15: hub("research", "What's the latest news on [your topic]? Cite your sources."),
  16: hub("chat", "Give me 3 dangers of trusting AI blindly — be specific."),
  17: hub("chat", "Take this messy text and clean it up into a numbered list: [paste]"),
  18: home("Using AI daily (habit formation)", "What's one thing I can ask AI every morning to save 10 minutes?"),
  19: hub("chat", "Look at my typical day: [list]. Where could AI save me an hour?"),
  20: hub("research", "I would normally Google this — answer it directly: [your question]"),

  // ───────── Application 21–40 ─────────
  21: { surface: "/planner", cta: "Open the planner", microPrompt: "Goal: 'Send a polite follow-up to a client who hasn't paid.'" },
  22: hub("builder", "Turn this rough idea into a sharp blog post outline: [your idea]", "Open builder"),
  23: hub("chat", "Here's my work history [paste] — write me a one-paragraph LinkedIn intro."),
  24: hub("chat", "Outline a 5-slide deck for: [your topic]. Title + 3 bullets per slide."),
  25: hub("chat", "I write the same kind of message every week. Help me make a reusable template."),
  26: hub("chat", "Write 3 short cold-email openers for selling [your product] to [your audience]."),
  27: hub("chat", "A customer says '[objection]'. Give me 3 ways to respond."),
  28: hub("research", "Who are the top 5 competitors for [your product]? What do customers complain about?"),
  29: { surface: "/planner", cta: "Open the planner", microPrompt: "Goal: 'Take an idea from notes to published post in one afternoon.'" },
  30: hub("chat", "I want to start [your idea]. Poke 5 holes in it before I waste time."),
  31: { surface: "/quests", cta: "Pick a quest", microPrompt: "Quests teach a skill in 10–20 minutes — pick anything that looks fun." },
  32: { surface: "/library", cta: "Open the library", microPrompt: "Fork any playbook — it becomes yours, editable, runnable." },
  33: hub("analyze", "Paste any meeting transcript — get decisions, action items, and owners."),
  34: hub("chat", "Help me design a daily note template I'll actually stick to."),
  35: hub("chat", "I have to choose between A and B. Score each one 1–10 across 4 criteria."),
  36: hub("image", "A neon retro arcade poster for a band called 'Midnight Static'."),
  37: { surface: "/?skill=AI%20video%20basics%20with%20Runway%20ML", cta: "Open the guided chat", microPrompt: "Learn what AI video can and can't do today." },
  38: hub("chat", "I have one blog post — turn it into 5 tweets, 1 LinkedIn post, and a YouTube hook."),
  39: hub("chat", "Design me a 3-app productivity stack: capture, plan, ship. Justify each pick."),
  40: hub("analyze", "I'm dumping my notes here — start building my second brain by tagging themes."),

  // ───────── Leverage 41–60 ─────────
  41: hub("builder", "Turn 'help me sell my course' into a 4-step prompt chain.", "Open builder"),
  42: hub("builder", "Build a 3-step chain: research → outline → draft. Topic: [your topic]"),
  43: { surface: "/power21", cta: "Open Power21", microPrompt: "Run a multi-step sprint — Power21 chains tasks across days." },
  44: hub("research", "Build me a research brief on [topic]: 5 sources, 3 contradictions, 1 conclusion."),
  45: { surface: "/library", cta: "Open the library", microPrompt: "Fork the 'Content Engine' playbook — runs daily." },
  46: { surface: "/power21", cta: "Open Power21", microPrompt: "Power21 has a built-in SEO sprint — start there." },
  47: hub("chat", "Take this LinkedIn profile [paste] and write a personalized opener about their work."),
  48: hub("research", "Find 10 [your ICP] in [city] who recently [trigger event]."),
  49: { surface: "/agents/builder?template=beginner", cta: "Open the agent builder", microPrompt: "Make an agent that drafts code snippets for any task you describe." },
  50: home("Using Zapier for automation", "What's the simplest Zap I can build to automate one repetitive task?"),
  51: home("Using Make (Integromat) for workflows", "Show me a Make scenario that posts a daily summary to Slack."),
  52: home("Connecting tools together (APIs conceptually)", "Explain APIs like I've never coded — using a restaurant analogy."),
  53: hub("analyze", "Here's a customer list [paste] — group them into 3 personas with names."),
  54: { surface: "/projects", cta: "Open projects", microPrompt: "Start a project to track an experiment over time." },
  55: hub("chat", "Read these 5 things I wrote [paste]. Describe my voice in 3 adjectives, then write a new paragraph in it."),
  56: { surface: "/library", cta: "Open the library", microPrompt: "Fork a playbook to make it your team's standard." },
  57: hub("builder", "Help me brainstorm 10 product ideas in [your space], then score each."),
  58: hub("builder", "Sketch a v0 of [your idea] — features, user flow, the riskiest assumption."),
  59: hub("chat", "Run an A/B test on my last reply — give me 3 alternative versions."),
  60: home("Building repeatable systems", "Take one of my one-off wins and turn it into a checklist anyone could follow."),

  // ───────── Systems 61–80 ─────────
  61: { surface: "/library", cta: "Open the library", microPrompt: "Read the 'API basics' playbook — concepts, not code." },
  62: home("Calling APIs (basic)", "Walk me through making my first API call with a real example."),
  63: home("Working with OpenAI API", "What does 'temperature' actually do? Show me with two outputs."),
  64: home("Using embeddings + vector databases", "Explain embeddings using a library analogy — no math."),
  65: { surface: "/agents/builder", cta: "Build an agent", microPrompt: "Build a chatbot for your specific niche — it remembers context." },
  66: { surface: "/agents/builder", cta: "Build an agent with tools", microPrompt: "Add web_search + web_fetch and give it a real research goal." },
  67: home("Multi-agent systems", "Describe a 2-agent setup: one researcher, one writer. How do they hand off?"),
  68: home("Memory systems for AI", "Walk me through how an agent 'remembers' across sessions."),
  69: { surface: "/agents/builder", cta: "Build an internal tool", microPrompt: "Build an agent that does one repeating task at your job." },
  70: { surface: "/projects", cta: "Open projects", microPrompt: "Document a process you do weekly — then automate one step." },
  71: home("AI + CRM integration", "What are the 3 most valuable AI integrations for a CRM?"),
  72: home("AI for customer support systems", "Sketch a tier-1 support bot that escalates well."),
  73: { surface: "/power21", cta: "Open Power21", microPrompt: "Power21 IS workflow orchestration — design one." },
  74: home("Data pipelines for AI", "Explain a data pipeline for AI like I run a kitchen."),
  75: home("Fine-tuning models (conceptual + practical)", "When does fine-tuning beat prompting? Give me 3 examples."),
  76: { surface: "/?skill=Ship%20an%20MVP%20with%20Lovable", cta: "Open the guided chat", microPrompt: "Build an MVP with Lovable — we'll guide you." },
  77: home("Using low-code frameworks", "Compare 3 no-code AI app builders — pros, cons, who they're for."),
  78: home("Debugging AI systems", "My agent gives wrong answers half the time. How do I diagnose it?"),
  79: home("Evaluating AI performance", "Design a 5-prompt eval for a customer-support bot."),
  80: home("Designing scalable workflows", "What breaks when you go from 10 users to 10,000?"),

  // ───────── Architecture 81–100 ─────────
  81: { surface: "/projects", cta: "Start a project", microPrompt: "Sketch the AI-first version of a business you know well." },
  82: { surface: "/projects", cta: "Open projects", microPrompt: "Outline an AI product: who, problem, riskiest assumption." },
  83: home("Creating proprietary datasets", "What unique data do I touch every day that nobody else has?"),
  84: home("Designing AI user experiences", "Critique the UX of any AI app I've used recently."),
  85: home("Human + AI interaction models", "Sketch 3 patterns: AI as oracle, copilot, autopilot."),
  86: home("AI ethics at scale", "What's one ethical question I'd face shipping AI to 1M users?"),
  87: home("Security + privacy in AI systems", "Top 5 things to lock down before going live with an AI feature."),
  88: home("AI monetization strategies", "Compare per-seat, usage-based, and outcome-based pricing for AI."),
  89: { surface: "/squads", cta: "Open squads", microPrompt: "Start a squad — agencies are squads with billing." },
  90: home("Scaling AI infrastructure", "What changes between 10 and 10M API calls a day?"),
  91: home("AI + organizational transformation", "Pick a department — describe its AI-augmented version."),
  92: home("Replacing departments with AI systems", "Which dept of a 10-person company would I AI-augment first, and why?"),
  93: { surface: "/projects", cta: "Open projects", microPrompt: "Design an AI ecosystem: what's core, what's open, what's API." },
  94: home("Multi-product AI platforms", "Sketch a 3-product AI platform — what's the unifying wedge?"),
  95: home("AI-driven marketplaces", "What makes an AI marketplace defensible vs a directory?"),
  96: home("Network effects with AI", "Map 3 ways AI products get better with more users."),
  97: home("Autonomous AI businesses", "Describe a business with 1 human and 100 agents — what's their day?"),
  98: home("AI + human augmentation frameworks", "Sketch a framework for what humans should still do, always."),
  99: home("Creating new categories with AI", "Name a category that didn't exist 3 years ago — would it work?"),
  100: { surface: "/profile/me", cta: "Share your run", microPrompt: "100/100. Take a victory lap — your full hacker journey." },
};

/** Lookup a skill's surface, cta, and micro-prompt. Returns null if unknown. */
export function surfaceForSkill(num: number): SkillSurface | null {
  return MASTERY_SURFACE_MAP[num] ?? null;
}